/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { MenuData, RootMenuLevel } from '../models';
import MegaMenuElement from './MegaMenuElement';
import MegaMenuExtra from './MegaMenuExtra';
import { MenuBlock } from '../MenuBlock';

interface MegaMenuModalProps {
    data: MenuData;
    activePath: number[];
    onActivate: (path: number[]) => void;
    onClose: () => void;
}

interface MegaMenuModalSnapshot {
    shouldFocus: boolean;
}

export default class MegaMenuModal extends React.Component<MegaMenuModalProps, unknown> {
    private readonly modalRef: React.RefObject<HTMLDivElement>;
    private readonly menuRef: React.RefObject<HTMLDivElement>;

    constructor(props: MegaMenuModalProps) {
        super(props);
        this.modalRef = React.createRef();
        this.menuRef = React.createRef();
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    }

    componentDidMount(): void {
        document.addEventListener('keydown', this.onDocumentKeydown);
    }

    getSnapshotBeforeUpdate(prevProps: Readonly<MegaMenuModalProps>): MegaMenuModalSnapshot | null {
        const prevSubtree = this.getActiveSubtree(prevProps.data, prevProps.activePath);
        const currentSubtree = this.getActiveSubtree(this.props.data, this.props.activePath);

        if (prevSubtree !== currentSubtree && currentSubtree) {
            return {
                shouldFocus: true,
            };
        }

        return null;
    }

    componentDidUpdate(
        prevProps: Readonly<MegaMenuModalProps>,
        prevState: Readonly<unknown>,
        snapshot?: MegaMenuModalSnapshot
    ): void {
        if (snapshot && snapshot.shouldFocus) {
            this.modalRef.current?.querySelector('a')?.focus();
        }

        // if (this.modalRef.current && this.menuRef.current) {
        //     this.modalRef.current.style.height = this.menuRef.current.scrollHeight + 'px';
        // }
    }

    render(): JSX.Element | null {
        const { activePath, data } = this.props;
        const activeSubtree = this.getActiveSubtree(data, activePath);

        if (!activeSubtree) return null;
        if (!activeSubtree.children) return null;

        return (
            <div ref={this.modalRef} className={`mega-menu__modal mega-menu__modal--${activeSubtree.viewMode}`}>
                <div ref={this.menuRef} className="mega-menu__modal-menu">
                    {activeSubtree.children.map((element, index) => (
                        <MegaMenuElement
                            key={index}
                            element={element}
                            level={2}
                            onActivate={(path) => this.onActivate(index, path)}
                            isActive={activePath[1] === index}
                            activePath={activePath}
                        />
                    ))}
                </div>
                {activePath.length && <MegaMenuExtra data={data} activePath={activePath} />}
                <div className="mega-menu__modal-block">
                    <MenuBlock type={data[activePath[0]!]!.blockType} />
                </div>
            </div>
        );
    }

    getActiveSubtree(data: MenuData, activePath: number[]): RootMenuLevel | null {
        if (activePath.length === 0) return null;
        const activeSubtreeIndex = activePath[0];

        if (typeof activeSubtreeIndex === 'undefined') return null;
        return data[activeSubtreeIndex] || null;
    }

    onActivate(index: number, path: number[]): void {
        this.props.onActivate([index, ...path]);
    }

    onDocumentKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.props.activePath.length) {
            this.props.onClose();
        }
    }
}
